/**
 * Shell chrome visibility — lets a route ask the shell to hide its own chrome (desktop top bar, widget dock,
 * mobile bottom nav) so a game can fill the window ("immersive mode"), and briefly reveal the bar again
 * from an in-game handle.
 *
 * A request is scoped to a path prefix: the shell honours it only while the current pathname is inside that
 * prefix, so navigating away restores the chrome even if the requester never cleared it. Pure module (no
 * React import beyond the hook): the shell reads it on every render; requesters (Villagr ui/immersive.ts) write it.
 */
import { useSyncExternalStore } from "react";

export interface ShellChromeState {
  /** Path prefix that asked for the chrome to be hidden, or null (chrome shown). */
  hiddenFor: string | null;
  /** While hidden: the bar is temporarily revealed as an overlay (hover/tap on the game's handle). */
  revealed: boolean;
}

const INITIAL: ShellChromeState = { hiddenFor: null, revealed: false };
let state: ShellChromeState = INITIAL;
const listeners = new Set<() => void>();
let hideTimer: ReturnType<typeof setTimeout> | null = null;
/** What is keeping the revealed bar up: the pointer is on it, and/or focus is inside it (e.g. its search box). */
export type ShellChromeHold = "pointer" | "focus";
const holds = new Set<ShellChromeHold>();

/** How long a revealed bar stays up without the pointer on it. */
export const REVEAL_MS = 4000;

function set(next: ShellChromeState): void {
  if (next.hiddenFor === state.hiddenFor && next.revealed === state.revealed) return;
  state = next;
  for (const l of listeners) l();
}
function clearTimer(): void {
  if (hideTimer !== null) { clearTimeout(hideTimer); hideTimer = null; }
}

export function getShellChrome(): ShellChromeState { return state; }
export function subscribeShellChrome(l: () => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

/** Hide the shell chrome while the pathname is under `prefix`; null shows it again (and drops any reveal). */
export function setShellChromeHidden(prefix: string | null): void {
  clearTimer();
  holds.clear();
  set({ hiddenFor: prefix, revealed: false });
}

/** Reveal the hidden bar for `ms` (re-arms the timer if already revealed). No-op while the chrome is shown. */
export function revealShellChrome(ms: number = REVEAL_MS): void {
  if (state.hiddenFor === null) return;
  clearTimer();
  holds.clear(); // a fresh reveal comes from the game's handle, never from inside the bar
  set({ ...state, revealed: true });
  hideTimer = setTimeout(() => { hideTimer = null; set({ ...state, revealed: false }); }, ms);
}
/** Pointer / focus is on the revealed bar: keep it up until released. */
export function holdShellChrome(kind: ShellChromeHold = "pointer"): void { holds.add(kind); clearTimer(); }
/** Pointer / focus left the revealed bar: once nothing holds it, hide it after a short grace period. */
export function releaseShellChrome(ms = 600, kind: ShellChromeHold = "pointer"): void {
  holds.delete(kind);
  if (!state.revealed || holds.size > 0) return;
  clearTimer();
  hideTimer = setTimeout(() => { hideTimer = null; set({ ...state, revealed: false }); }, ms);
}
export function hideRevealedShellChrome(): void { clearTimer(); holds.clear(); set({ ...state, revealed: false }); }

/** Spread on the revealed bar's wrapper. Only a MOUSE leave releases it: touch/pen fire pointerleave right after the
 *  finger lifts, which would unmount the Apps drawer / account menu mid-use — a touch reveal is dismissed by
 *  {@link dismissRevealOnPagePress} instead. Focus inside the bar (keyboard, search box) holds it too. */
export const revealHold = {
  onPointerEnter: (): void => holdShellChrome("pointer"),
  onPointerLeave: (e: { pointerType: string }): void => { if (e.pointerType === "mouse") releaseShellChrome(600, "pointer"); },
  onFocus: (): void => holdShellChrome("focus"),
  onBlur: (e: { currentTarget: { contains(n: Node | null): boolean }; relatedTarget: unknown }): void => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) releaseShellChrome(600, "focus");
  },
};
/** Capture-phase pointerdown on the requesting page's own area (the game, not the bar or its portalled menus): a
 *  touch/pen press there hides a revealed bar. Mouse reveals end on the leave timer instead. */
export function dismissRevealOnPagePress(e: { pointerType: string }): void {
  if (e.pointerType === "mouse" || !state.revealed) return;
  hideRevealedShellChrome();
}

/** Whether the chrome is hidden for `pathname` (prefix match on a path-segment boundary). */
export function isShellChromeHidden(s: ShellChromeState, pathname: string): boolean {
  const p = s.hiddenFor;
  if (!p) return false;
  return pathname === p || pathname.startsWith(p.endsWith("/") ? p : p + "/");
}

export function useShellChrome(): ShellChromeState {
  return useSyncExternalStore(subscribeShellChrome, getShellChrome, getShellChrome);
}

/** Test-only: reset to the initial state. */
export function __resetShellChrome(): void { clearTimer(); holds.clear(); state = INITIAL; listeners.clear(); }
